import React, { useEffect, useState } from "react";
import { Space, Button, Table, message, Select } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import StepHeader from "../../Evaluation/StepHeader";
import moment from "moment";
import { TestSetService } from "../../../services/TestSetService";
import { usePermissions } from "../../../PermissionsProvider";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { RequestViewButton } from "../../shared/button/Buttons";

interface TestSetRequestsProps {
  onChange: (requests: ChatCompletionRequest[]) => void;
  intentGroupId: number;
  initialSelectedRequests?: ChatCompletionRequest[];
}

const TestSetRequests: React.FC<TestSetRequestsProps> = ({
  onChange,
  intentGroupId,
  initialSelectedRequests = [],
}) => {
  const { apiKey } = usePermissions();
  const [eligibleRequests, setEligibleRequests] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    initialSelectedRequests.map((r) => r.request_id)
  );
  const [cachedRequests, setCachedRequests] = useState<
    Record<number, { items: any[]; total: number }>
  >({});
  const [selectedRows, setSelectedRows] = useState<ChatCompletionRequest[]>(
    initialSelectedRequests
  );
  const [initialLoadDate] = useState<number>(Date.now());

  const updateTestSetRequest = (req: ChatCompletionRequest) => {
    // Update the request in eligibleRequests (table data)
    setEligibleRequests((prev) =>
      prev.map((r) => (r.request_id === req.request_id ? req : r))
    );

    // Update cached requests to maintain consistency
    setCachedRequests((prev) => {
      const updatedCache = { ...prev };
      Object.keys(updatedCache).forEach((page: any) => {
        updatedCache[page] = {
          ...updatedCache[page],
          items: updatedCache[page].items.map((r: any) =>
            r.request_id === req.request_id ? req : r
          ),
        };
      });
      return updatedCache;
    });

    // Update selected rows
    setSelectedRows((prev) => {
      const updatedRows = prev.map((r) =>
        r.request_id === req.request_id ? req : r
      );
      if (!prev.some((r) => r.request_id === req.request_id)) {
        updatedRows.push(req);
      }
      return updatedRows;
    });

    // Ensure the row is selected in the table
    setSelectedRowKeys((prev) => {
      if (!prev.includes(req.request_id)) {
        return [...prev, req.request_id];
      }
      return prev;
    });

    // Call onChange with the updated rows after state updates
    setSelectedRows((current) => {
      onChange(current);
      return current;
    });
  };

  useEffect(() => {
    if (cachedRequests[currentPage]) {
      setEligibleRequests(cachedRequests[currentPage].items);
      setTotal(cachedRequests[currentPage].total);
    } else if (intentGroupId && Number(intentGroupId) > 0) {
      fetchEligibleRequests(currentPage);
    }
  }, [currentPage, intentGroupId]);

  const fetchEligibleRequests = (page: number) => {
    const offset = (page - 1) * 10;
    setLoading(true);
    TestSetService.getEligibleTestSetRequests(
      intentGroupId,
      -1,
      initialLoadDate,
      offset,
      10,
      null,
      setLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setEligibleRequests(data.items || []);
        setTotal(data.total || 0);
        setCachedRequests((prev) => ({
          ...prev,
          [page]: { items: data.items || [], total: data.total || 0 },
        }));
        setLoading(false);
      },
      (error: any) => {
        console.error(error);
        setLoading(false);
      }
    );
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    setSelectedRows((prev) => {
      const updatedRows = prev.map((row) => {
        if (row.request_id === requestId) {
          return {
            ...row,
            metadata: {
              _maitai_test_request_meta: JSON.stringify({ tags: newTags }),
            },
          };
        }
        return row;
      });
      onChange(updatedRows);
      return updatedRows;
    });
  };

  const columns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Request ID",
      dataIndex: "request_id",
      align: "right" as const,
      render: (text: string) => (
        <Space>
          {text}
          <Button
            icon={<CopyOutlined />}
            size="small"
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(text);
              message.success("Request ID copied to clipboard");
            }}
          />
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <RequestViewButton
          buttonStyle={{ color: "#21b892" }}
          apiKey={apiKey}
          request={record}
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}
          isEditable={true}
          onUpdated={(req: ChatCompletionRequest) => {
            updateTestSetRequest(req);
          }}>
          View
        </RequestViewButton>
      ),
    },
    {
      title: "Tags",
      key: "tags",
      width: "30%",
      render: (_: any, record: any) => {
        const isSelected = selectedRowKeys.includes(record.request_id);
        const selectedRow = selectedRows.find(
          (row) => row.request_id === record.request_id
        );
        let tags: string[] = [];

        if (selectedRow?.metadata?._maitai_test_request_meta) {
          try {
            const meta = JSON.parse(
              selectedRow.metadata._maitai_test_request_meta as string
            );
            tags = meta.tags || [];
          } catch (e) {
            console.error("Error parsing metadata:", e);
          }
        }

        return isSelected ? (
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder="Add tags"
            value={tags}
            tokenSeparators={[","]}
            dropdownStyle={{ display: "none" }}
            onClick={(e) => e.stopPropagation()}
            maxTagTextLength={10}
            maxTagCount={5}
            onChange={(newTags) => handleTagsChange(record.request_id, newTags)}
          />
        ) : (
          <span
            style={{ color: "#999", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              const newSelectedRowKeys = [
                ...selectedRowKeys,
                record.request_id,
              ];
              const newSelectedRows = [
                ...selectedRows,
                record as ChatCompletionRequest,
              ];

              setSelectedRowKeys(newSelectedRowKeys);
              setSelectedRows(newSelectedRows);
              onChange(newSelectedRows);
            }}>
            Select request to add tags
          </span>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (
      newSelectedRowKeys: React.Key[],
      newSelectedRows: ChatCompletionRequest[]
    ) => {
      setSelectedRowKeys(newSelectedRowKeys);

      const remainingRows = selectedRows.filter((row) =>
        newSelectedRowKeys.includes(row.request_id)
      );

      const updatedRows = [
        ...remainingRows,
        ...newSelectedRows.filter(
          (newRow) =>
            !remainingRows.some(
              (existingRow) => existingRow.request_id === newRow.request_id
            )
        ),
      ];

      setSelectedRows(updatedRows);
      onChange(updatedRows);
    },
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <StepHeader
        title="Requests"
        description="Select requests to include in your test set"
      />

      <Table
        rowSelection={rowSelection}
        dataSource={eligibleRequests}
        columns={columns}
        rowKey="request_id"
        pagination={{
          current: currentPage,
          pageSize: 10,
          total: total,
          onChange: (page) => setCurrentPage(page),
        }}
        loading={loading}
      />
    </Space>
  );
};

export default TestSetRequests;
